import { Component, ElementRef, Input, OnDestroy, OnInit, Output, ChangeDetectorRef, HostListener, ViewChild } from '@angular/core';
import { trigger, style, animate, transition, query, AnimationEvent, sequence, keyframes } from '@angular/animations';
import { NavigationEnd, Router } from '@angular/router';

import { Observable, Subject, Subscription } from 'rxjs';
import { DomHandler } from 'primeng/dom';
import * as uuid from 'uuid';

import { AbstractDialogComponent } from '../../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { BaseJournodexEntityModel } from '../../../../../Discover/Targeting/models/base-journodex-entity.model';
import { EntityType } from '../../../../../api/targeting/enum/entity.type.enum';
import { OutletModel } from '../../../../../Discover/Targeting/models/outlet.model';
import { ContactModel } from '../../../../../Common/TargetingShared/model/contact.model';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { ContactService } from '../../../../../Common/TargetingShared/services/contact-service';
import { TargetingSearchService } from '../../../../../Discover/Targeting/services/targeting-search.service';
import { PrivateDataModel } from '../../../../../Discover/Targeting/models/private-data.model';
import { TargetingRhpContextsService } from '../../../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { TargetingRhpDialogService } from '../../../../../Common/TargetingShared/services/targeting-rhp-dialog.service';
import { InteractionViewService } from '../../../services/interaction-view.service';
import { EntityFullProfileComponent } from '../entity-full-profile.component';
import { ButtonTypesEnum } from '../../../../Forms/enums/button-types.enum';

const DEFAULT_CONTAINER_WIDTH = 483;

@Component({
    selector: 'agpr-full-profile-dialog',
    templateUrl: './full-profile-dialog.component.html',
    styleUrls: ['./full-profile-dialog.component.scss'],
    providers: [TargetingRhpContextsService, InteractionViewService],
    animations: [
        trigger(FullProfileDialogAnimationsEnum.slideInOutAnimation, [
            transition('* => open', [
                query(':self', [
                    style({ position: 'fixed', width: '100%', left: 'calc(100vw - {{sidebarWidth}}px)', opacity: 0 }),
                    sequence([
                        animate('0.20s cubic-bezier(0.75, 0.25, 0, 1.05)', style({ left: '0', opacity: 1 }))
                    ])
                ]),
            ]),
            transition('* => close', [
                query(':self', [
                    style({ position: 'fixed', width: '100%', left: '0', opacity: 1 }),
                    sequence([
                        animate('0.20s cubic-bezier(0.75, 0.25, 0, 1.05)', style({ left: 'calc(100vw - {{sidebarWidth}}px)', opacity: 0 })),
                    ])
                ])
            ])
        ]),
        trigger(FullProfileDialogAnimationsEnum.overlayAnimation, [
            transition('* => open', [
                style({ opacity: 0 }),
                sequence([
                    animate('0.15s', style({ opacity: 1 })),
                ])
            ]),
            transition('* => close', [
                style({ opacity: 1 }),
                sequence([
                    animate('0.15s 0.3s', style({ opacity: 0 })),
                ])
            ])
        ]),
        trigger('fade', [
            transition('* => true', [
                animate('300ms ease-in-out', keyframes([
                    style({
                        opacity: 0
                    }),
                    style({
                        opacity: 1
                    })
                ]))
            ]),
            transition('true => *', [
                animate('300ms ease-in-out', keyframes([
                    style({
                        opacity: 1
                    }),
                    style({
                        opacity: 0
                    })
                ]))
            ])
        ])
    ],
    standalone: false
})
export class FullProfileDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;

    @Input() backButtonLocalization: string;
    @Input() sidebarRef: ElementRef;
    @Input() entityType: EntityType;
    @Input() set outlet (outlet: OutletModel) {
        if (outlet) {
            this.entity = outlet;
        }
    }
    @Input() set contact (contact: ContactModel) {
        if (contact) {
            this.entity = contact;
        }
    }
    @Input() contactPrivateData: PrivateDataModel;
    @Input() outletPrivateData: PrivateDataModel;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() hideBackButton: boolean = true;
    @Input() fullHeight: boolean = false;

    @Output() animationStateChange: Subject<OverlayAnimationStateEnum> = new Subject();

    subscriptions: Subscription[] = [];
    slideInOutAnimation: {value: OverlayAnimationStateEnum; params: {sidebarWidth: number}};
    overlayAnimation: OverlayAnimationStateEnum;
    entity: BaseJournodexEntityModel;
    zIndex: number;
    contactEditorZIndex: number;
    contactEditorVisible: boolean = false;
    newSelectedEmploymentId: number;
    panelHeight: string;
    navBar: Element;

    navHeightObserver = new ResizeObserver((entry) => {
        const navHeight = entry[0].contentRect.height;
        this.calculateHeight(navHeight);
    });

    @ViewChild('entityFullProfile') entityFullProfile: EntityFullProfileComponent;

    constructor(
        public contactService: ContactService,
        public coreDialogService: CoreDialogService,
        private router: Router,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public targetingSearchService: TargetingSearchService,
        public cd: ChangeDetectorRef,
        public targetingRhpDialogService: TargetingRhpDialogService
    ) {
        super(coreDialogService, uuid.v4());
        this.zIndex = ++DomHandler.zindex;
        this.contactEditorZIndex = ++DomHandler.zindex;
    }

    windowResizeHandler = () => {
        this.calculateHeight(this.navBar.clientHeight);
    };

    ngOnInit() {
        this.subscriptions.push(
            this.router.events.subscribe((e) => {
                if (e instanceof NavigationEnd) {
                    this.targetingRhpDialogService.changeTitleOnClose = false;
                    this.overlayAnimation = OverlayAnimationStateEnum.close;
                }
            })
        );
        if (this.animationTrigger) {
            this.subscriptions.push(
                this.animationTrigger.subscribe(animationModel => {
                    if (animationModel.animation === FullProfileDialogAnimationsEnum.overlayAnimation) {
                        this.overlayAnimation = OverlayAnimationStateEnum.open;
                    }
                    if (animationModel.animation === FullProfileDialogAnimationsEnum.slideInOutAnimation) {
                        this.overlayAnimationStart(<AnimationEvent> {
                            fromState: null,
                            toState: OverlayAnimationStateEnum.open
                        });
                    }
                })
            );
        }
        if (this.fullHeight) {
            this.panelHeight = '100%';
        } else {
            this.navBar = document.querySelector('.agpr-masterLayout-navigation');
            this.navHeightObserver.observe(this.navBar);
            window.addEventListener('resize', this.windowResizeHandler);
            this.calculateHeight(this.navBar.clientHeight);
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.navHeightObserver.disconnect();
        window.removeEventListener('resize', this.windowResizeHandler);
    }

    close(newSelectedEmploymentId: number) {
        this.newSelectedEmploymentId = newSelectedEmploymentId;
        this.overlayAnimation = OverlayAnimationStateEnum.close;
    }

    closeAll(): void {
        this.coreDialogService.closeAllExceptKeys(['ChartDrillDownDialog']);
    }

    animationDone(event: AnimationEvent) {
        if (event.toState === OverlayAnimationStateEnum.close) {
            this.closeDialog({
                fullProfileDialogKey: this.key,
                newSelectedEmploymentId: this.newSelectedEmploymentId
            });

            if (this.newSelectedEmploymentId) {
                this.targetingRhpContextsService.changeSelectedEmployment(this.newSelectedEmploymentId);
            }
        }

        if (
            event.triggerName === FullProfileDialogAnimationsEnum.slideInOutAnimation
            &&  event.toState === OverlayAnimationStateEnum.open
        ) {
            this.animationStateChange.next(OverlayAnimationStateEnum.open);
        }
    }

    overlayAnimationStart(event: AnimationEvent) {
        if (event.toState === OverlayAnimationStateEnum.open) {
            const sidebarWidth = this.sidebarRef?.nativeElement?.parentElement?.offsetWidth
                ? this.sidebarRef.nativeElement.parentElement.offsetWidth
                : DEFAULT_CONTAINER_WIDTH;

            this.slideInOutAnimation = {
                value: OverlayAnimationStateEnum.open,
                params: {
                    sidebarWidth: sidebarWidth
                }
            };
        }

        if (event.toState === OverlayAnimationStateEnum.close) {

            const sidebarWidth = this.sidebarRef?.nativeElement?.parentElement?.offsetWidth
                ? this.sidebarRef.nativeElement.parentElement.offsetWidth
                : DEFAULT_CONTAINER_WIDTH;

            this.slideInOutAnimation = {
                value: OverlayAnimationStateEnum.close,
                params: {
                    sidebarWidth: sidebarWidth
                }
            };
        }
    }

    showContactEditor() {
        this.contactEditorVisible = true;
    }

    onContactEditorClose(id: string) {
        this.contactEditorVisible = false;
        this.targetingRhpContextsService.setContact(id);
        this.contactService.clearEditingContact();
    }

    calculateHeight(navHeight: number) {
        this.panelHeight = (window.innerHeight - navHeight) + 'px';
        this.cd.detectChanges();
    }
}
